import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { FaInstagram, FaFacebook, FaTiktok, FaYoutube, FaXTwitter, FaPinterest, FaLinkedin } from 'react-icons/fa6';
import './Footer.css';

const ICON_MAP = {
  instagram: <FaInstagram />,
  facebook: <FaFacebook />,
  tiktok: <FaTiktok />,
  youtube: <FaYoutube />,
  twitter: <FaXTwitter />,
  pinterest: <FaPinterest />,
  linkedin: <FaLinkedin />,
};

const normalizeUrl = (url) => {
  if (!url) return '#';
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const Footer = () => {
  const [settings, setSettings] = useState({
    footer_text: '© 2026 UrbanThread. All rights reserved.',
    store_email: 'hello@urbanthread.pk',
    store_phone: '+92 300 0000000',
    store_name: 'URBANTHREAD',
    store_description: 'Modern menswear essentials designed for everyday style, comfort, and confidence.'
  });
  const [version, setVersion] = useState(Date.now());

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php', {
        cache: 'no-store'
      });
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(prev => ({ ...prev, ...data.settings }));
        if (data.version) setVersion(data.version);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = (settings.store_whatsapp || '923001234567').replace(/\D/g, '');
    const message = 'Hi! I would like to know more about your products.';
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  let socialLinks = [];
  if (settings.social_links) {
    try {
      socialLinks = JSON.parse(settings.social_links);
    } catch (e) {
      console.error('Error parsing social_links:', e);
    }
  }
  const activeSocialLinks = socialLinks.filter(l => l.url);

  return (
    <footer className="footer">
      <div className="footer-container footer-container-3col">
        {/* Brand Section */}
        <div className="footer-section brand-section">
          {settings.footer_logo ? (
            <img src={`${settings.footer_logo}?v=${version}`} alt={settings.store_name || 'Logo'} className="footer-logo-img" />
          ) : (
            <h3 className="footer-brand">{settings.store_name || 'URBANTHREAD'}</h3>
          )}
          <p className="footer-description">{settings.store_description || 'Modern menswear essentials designed for everyday style, comfort, and confidence.'}</p>

          {activeSocialLinks.length > 0 && (
            <div className="footer-social-icons">
              {activeSocialLinks.map((link, i) => (
                <a
                  key={i}
                  href={normalizeUrl(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.platform}
                  className="footer-social-icon"
                >
                  {ICON_MAP[link.platform] || null}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Shop Links */}
        <div className="footer-section">
          <h4 className="footer-title">SHOP</h4>
          <ul className="footer-links">
            <li><Link to="/shop?sort=newest">New Arrivals</Link></li>
            <li><Link to="/shop?category=shirts">Shirts</Link></li>
            <li><Link to="/shop?category=t-shirts">T-Shirts</Link></li>
            <li><Link to="/shop?category=trousers">Trousers</Link></li>
            <li><Link to="/shop?filter=sale">Sale</Link></li>
          </ul>
        </div>

        {/* Support Links — Company section merged in: Privacy & Terms
            moved here, FAQ and Contact Us removed (Contact is now its
            own dedicated section below with real contact details). */}
        <div className="footer-section">
          <h4 className="footer-title">SUPPORT</h4>
          <ul className="footer-links">
            <li><Link to="/shipping-policy">Shipping Policy</Link></li>
            <li><Link to="/return-exchange">Return & Exchange</Link></li>
            <li><Link to="/privacy-policy">Privacy Policy</Link></li>
            <li><Link to="/terms">Terms & Conditions</Link></li>
          </ul>
        </div>

        {/* Contact — redesigned as icon + label rows */}
        <div className="footer-section">
          <h4 className="footer-title">CONTACT</h4>
          <div className="footer-contact">
            <div className="footer-contact-row">
              <span className="footer-contact-icon"><FaPhoneAlt /></span>
              <a href={`tel:${(settings.store_phone || '').replace(/\s/g, '')}`} className="footer-contact-text">
                {settings.store_phone || '+92 300 0000000'}
              </a>
            </div>

            {settings.store_whatsapp && (
              <div className="footer-contact-row">
                <span className="footer-contact-icon footer-contact-icon-whatsapp"><FaWhatsapp /></span>
                <button type="button" className="footer-contact-text footer-contact-btn" onClick={handleWhatsAppClick}>
                  {settings.store_whatsapp}
                </button>
              </div>
            )}

            <div className="footer-contact-row">
              <span className="footer-contact-icon"><FaEnvelope /></span>
              <a href={`mailto:${settings.store_email || 'hello@urbanthread.pk'}`} className="footer-contact-text">
                {settings.store_email || 'hello@urbanthread.pk'}
              </a>
            </div>

            {settings.store_address && (
              <div className="footer-contact-row">
                <span className="footer-contact-icon"><FaMapMarkerAlt /></span>
                <span className="footer-contact-text">{settings.store_address}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-container">
          <p className="copyright">{settings.footer_text || '© 2026 UrbanThread. All rights reserved.'}</p>
          <div className="payment-methods-footer">
            {/* 
            <span className="payment-badge">COD</span>
            <span className="payment-badge">BANK TRANSFER</span>
            <span className="payment-badge">ONLINE PAYMENT</span>
            */}
          </div>
        </div>
      </div>

      <button className="whatsapp-float-btn" onClick={handleWhatsAppClick} aria-label="Chat on WhatsApp">
        <FaWhatsapp className="whatsapp-icon" />
        <span className="whatsapp-tooltip">Chat with us</span>
      </button>
    </footer>
  );
};

export default Footer;